import React, { useEffect, useMemo, useState } from 'react';
import { testingService } from '../../services/api';
import { stagePendingTestPlanImport } from '../ClinicDetail/pendingTestPlanImport';

const CREATE_NEW_PLAN_VALUE = '__create_new__';

const generateDashboardPlanName = () => {
  const now = new Date();
  const pad = (value) => String(value).padStart(2, '0');
  const datePart = `${now.getFullYear()}${pad(now.getMonth() + 1)}${pad(now.getDate())}`;
  const timePart = `${pad(now.getHours())}${pad(now.getMinutes())}${pad(now.getSeconds())}`;
  return `dashboard_${datePart}_${timePart}`;
};

const serializeCaseForApi = (caseItem) => ({
  name: String(caseItem?.name || '').trim(),
  messages: Array.isArray(caseItem?.messages) ? caseItem.messages : [],
  is_skipped: !!caseItem?.is_skipped,
  annotation: caseItem?.annotation || '',
  preview_pairs: Array.isArray(caseItem?.preview_pairs) ? caseItem.preview_pairs : [],
});

const AddToTestPlanModal = ({
  clinicId,
  cases = [],
  defaultDestination = 'append',
  onClose,
  onOpenInEditor,
  onAppended,
}) => {
  const [plans, setPlans] = useState([]);
  const [loadingPlans, setLoadingPlans] = useState(true);
  const [selectedPlanId, setSelectedPlanId] = useState('');
  const [newPlanName, setNewPlanName] = useState(() => generateDashboardPlanName());
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');

  const summary = useMemo(() => {
    const caseCount = Array.isArray(cases) ? cases.length : 0;
    const messageCount = (Array.isArray(cases) ? cases : []).reduce(
      (total, caseItem) => total + (Array.isArray(caseItem?.messages) ? caseItem.messages.length : 0),
      0
    );
    return { caseCount, messageCount };
  }, [cases]);

  useEffect(() => {
    if (!clinicId) return undefined;
    let cancelled = false;
    const loadPlans = async () => {
      setLoadingPlans(true);
      setError(null);
      try {
        const res = await testingService.listTestPlans(clinicId);
        if (cancelled) return;
        const nextPlans = Array.isArray(res?.plans) ? res.plans : [];
        setPlans(nextPlans);
        if (nextPlans.length > 0) {
          setSelectedPlanId(String(nextPlans[0].id));
        } else {
          setSelectedPlanId(CREATE_NEW_PLAN_VALUE);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err?.message || 'Failed to load test plans.');
        }
      } finally {
        if (!cancelled) setLoadingPlans(false);
      }
    };
    loadPlans();
    return () => { cancelled = true; };
  }, [clinicId]);

  const handleAppend = async () => {
    if (!clinicId || !Array.isArray(cases) || cases.length === 0) return;
    setIsSubmitting(true);
    setError(null);
    setSuccessMessage('');

    try {
      const serializedCases = cases.map(serializeCaseForApi);

      if (selectedPlanId === CREATE_NEW_PLAN_VALUE) {
        const planName = String(newPlanName || '').trim() || generateDashboardPlanName();
        const result = await testingService.createTestPlan({
          clid: clinicId,
          name: planName,
          description: '',
          cases: serializedCases,
        });
        if (!result?.success) {
          setError(result?.error || 'Failed to create test plan.');
          return;
        }
        setSuccessMessage(`Created plan "${planName}" with ${summary.caseCount} case(s).`);
        onAppended?.(result?.plan);
        window.setTimeout(() => onClose?.(), 1200);
        return;
      }

      const targetPlan = plans.find((plan) => String(plan.id) === String(selectedPlanId));
      if (!targetPlan) {
        setError('Selected test plan was not found.');
        return;
      }

      const existingCases = Array.isArray(targetPlan.cases)
        ? targetPlan.cases.map(serializeCaseForApi)
        : [];
      const payload = {
        clid: clinicId,
        name: targetPlan.name || '',
        description: targetPlan.description || '',
        cases: [...existingCases, ...serializedCases],
      };
      const result = await testingService.updateTestPlan(targetPlan.id, payload);
      if (!result?.success) {
        setError(result?.error || 'Failed to append cases to test plan.');
        return;
      }
      setSuccessMessage(`Added ${summary.caseCount} case(s) to "${targetPlan.name}".`);
      onAppended?.(result?.plan);
      window.setTimeout(() => onClose?.(), 1200);
    } catch (err) {
      setError(err?.message || 'Failed to update test plan.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleOpenInEditor = () => {
    if (!clinicId || !Array.isArray(cases) || cases.length === 0) return;
    const planId = selectedPlanId !== CREATE_NEW_PLAN_VALUE ? selectedPlanId : null;
    stagePendingTestPlanImport({ clid: clinicId, cases, planId });
    onOpenInEditor?.({ planId });
    onClose?.();
  };

  const handlePrimaryAction = () => {
    if (defaultDestination === 'editor') {
      handleOpenInEditor();
      return;
    }
    handleAppend();
  };

  if (!Array.isArray(cases) || cases.length === 0) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md mx-4">
          <p className="text-gray-700">No test cases to add.</p>
          <button
            type="button"
            onClick={onClose}
            className="mt-4 px-4 py-2 bg-gray-200 rounded hover:bg-gray-300"
          >
            Close
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-lg mx-4">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold text-gray-800">Add to test plan</h3>
          <button type="button" onClick={onClose} className="text-gray-500 hover:text-gray-700">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <p className="text-sm text-gray-600 mb-4">
          Adding {summary.caseCount} case{summary.caseCount === 1 ? '' : 's'}
          {' '}({summary.messageCount} user message{summary.messageCount === 1 ? '' : 's'} total)
        </p>

        <div className="space-y-3 mb-4">
          <label className="block text-sm font-medium text-gray-700">
            Test plan
            <select
              value={selectedPlanId}
              onChange={(e) => setSelectedPlanId(e.target.value)}
              disabled={loadingPlans || isSubmitting}
              className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#075e54]"
            >
              {loadingPlans && <option value="">Loading plans…</option>}
              {!loadingPlans && plans.length === 0 && (
                <option value={CREATE_NEW_PLAN_VALUE}>Create new plan…</option>
              )}
              {!loadingPlans && plans.map((plan) => (
                <option key={plan.id} value={String(plan.id)}>
                  {plan.name || plan.id}
                </option>
              ))}
              {!loadingPlans && plans.length > 0 && (
                <option value={CREATE_NEW_PLAN_VALUE}>Create new plan…</option>
              )}
            </select>
          </label>

          {selectedPlanId === CREATE_NEW_PLAN_VALUE && (
            <label className="block text-sm font-medium text-gray-700">
              New plan name
              <input
                type="text"
                value={newPlanName}
                onChange={(e) => setNewPlanName(e.target.value)}
                disabled={isSubmitting}
                className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#075e54]"
              />
            </label>
          )}
        </div>

        {error && (
          <p className="text-sm text-red-600 mb-3">{error}</p>
        )}
        {successMessage && (
          <p className="text-sm text-green-700 mb-3">{successMessage}</p>
        )}

        <div className="flex flex-wrap gap-2 justify-end">
          <button
            type="button"
            onClick={onClose}
            disabled={isSubmitting}
            className="px-4 py-2 text-sm rounded bg-gray-200 text-gray-800 hover:bg-gray-300 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleOpenInEditor}
            disabled={isSubmitting}
            className="px-4 py-2 text-sm rounded bg-purple-600 text-white hover:bg-purple-700 disabled:opacity-50"
          >
            Open in editor
          </button>
          <button
            type="button"
            onClick={handlePrimaryAction}
            disabled={isSubmitting || loadingPlans}
            className="px-4 py-2 text-sm rounded bg-[#075e54] text-white hover:bg-[#128c7e] disabled:opacity-50"
          >
            {isSubmitting ? 'Saving…' : (selectedPlanId === CREATE_NEW_PLAN_VALUE ? 'Create plan' : 'Add to plan')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AddToTestPlanModal;
